import { ref, computed, watch, nextTick } from "vue";
const DELAY_TO_SCROLL_CORRECT = 250;
function useListNavigation(options) {
  const {
    itemsList,
    itemsListRef,
    virtualizedList = false,
    horizontal = true,
    enabled = true,
    initialPosition = 0,
    callBack
  } = options || {};
  const itemsListNodes = ref([]);
  const isNavigatingByKeyboard = ref(false);
  const currentItemOfList = ref(null);
  const currentItemOfListIndex = ref(-1);
  const computedItemsList = computed(() => {
    return itemsList == null ? void 0 : itemsList.value.filter((item) => !(item == null ? void 0 : item.hidden));
  });
  function checkIfIsValidNode(node) {
    return !node.ariaReadOnly && !node.disabled && !node.ariaDisabled && !node.dataset.invalid && node.dataset.index;
  }
  const navigableNodes = computed({
    get() {
      const validNodes = itemsListNodes.value.map((node) => (node == null ? void 0 : node.$el) || node).filter((node) => node && checkIfIsValidNode(node));
      return validNodes;
    },
    set(newValue) {
      itemsListNodes.value = [...newValue];
    }
  });
  const isFirstNode = computed(() => currentNavigableNodeIndex.value === 0);
  const isLastNode = computed(
    () => {
      var _a;
      return currentNavigableNodeIndex.value === ((_a = navigableNodes.value) == null ? void 0 : _a.length) - 1;
    }
  );
  const directions = {
    prev: horizontal ? "ArrowLeft" : "ArrowUp",
    next: horizontal ? "ArrowRight" : "ArrowDown"
  };
  const canNavigate = computed(() => enabled && navigableNodes.value.length > 0);
  const currentNavigableNode = ref(null);
  const currentNavigableNodeIndex = ref(0);
  function emitCallback(event) {
    if (callBack && typeof callBack === "function") {
      callBack(currentItemOfList.value, event);
    }
  }
  function getItemIndexByNode(node) {
    var _a;
    return parseInt((_a = node == null ? void 0 : node.dataset) == null ? void 0 : _a.index, 10);
  }
  function getNodeIndex(index) {
    return navigableNodes.value.findIndex((node) => getItemIndexByNode(node) === index);
  }
  function setCurrentNavigableNodeIndex(index) {
    currentNavigableNodeIndex.value = index;
  }
  function setCurrentNavigableNode(index) {
    var _a;
    if (navigableNodes.value.length > 0) {
      currentNavigableNode.value = (_a = navigableNodes.value) == null ? void 0 : _a[index];
      setCurrentNavigableNodeIndex(index);
    }
  }
  function setCurrentItem() {
    currentItemOfListIndex.value = getItemIndexByNode(currentNavigableNode.value);
    if (computedItemsList == null ? void 0 : computedItemsList.value) {
      currentItemOfList.value = computedItemsList.value[currentItemOfListIndex.value];
    }
  }
  function scrollCurrentNavigableNodeIntoView(enableDelay = false) {
    const scrollViewPosition = enableDelay ? "center" : "nearest";
    const scrollAction = () => {
      var _a, _b;
      if ((_a = currentNavigableNode.value) == null ? void 0 : _a.scrollIntoView) {
        currentNavigableNode.value.scrollIntoView({
          behavior: "auto",
          block: scrollViewPosition,
          inline: scrollViewPosition
        });
      } else if ((_b = currentNavigableNode.value) == null ? void 0 : _b.scrollIntoViewIfNeeded) {
        currentNavigableNode.value.scrollIntoViewIfNeeded(true);
      }
    };
    if (enableDelay) {
      setTimeout(scrollAction, DELAY_TO_SCROLL_CORRECT);
    } else {
      scrollAction();
    }
  }
  function focusNavigableNode() {
    var _a;
    if (currentNavigableNode.value) {
      (_a = currentNavigableNode.value) == null ? void 0 : _a.focus();
    }
  }
  function getNodePositionWithLooplessNavigation(newNodePosition, totalNodes) {
    return Math.max(Math.min(newNodePosition, totalNodes - 1), 0);
  }
  function getNodePositionWithLoopNavigation(newNodePosition, totalNodes) {
    return (newNodePosition % totalNodes + totalNodes) % totalNodes;
  }
  function getNavigationDirection(keyCode) {
    const isPrev = keyCode === directions.prev;
    const isNext = keyCode === directions.next;
    const emptyDirection = !isNext && !isPrev;
    return {
      isPrev,
      isNext,
      emptyDirection
    };
  }
  function handleInitialItem(itemIndex = 0) {
    const navigate = ensureCanNavigate(() => {
      const newNodePosition = getNodeIndex(itemIndex);
      setCurrentNavigableNode(newNodePosition);
      scrollCurrentNavigableNodeIntoView(true);
      setCurrentItem();
    });
    navigate();
  }
  function handleNavigationByItemIndex(itemIndex = 0) {
    const navigate = ensureCanNavigate(() => {
      const newNodePosition = getNodeIndex(itemIndex);
      setCurrentNavigableNode(newNodePosition);
      scrollCurrentNavigableNodeIntoView();
      setCurrentItem();
      emitCallback();
    });
    navigate();
  }
  function setInitialFocusByNodeIndex(nodeIndex = 0) {
    nextTick(() => {
      setCurrentNavigableNode(nodeIndex);
      scrollCurrentNavigableNodeIntoView();
      setCurrentItem();
      emitCallback();
      focusNavigableNode();
      isNavigatingByKeyboard.value = true;
    });
  }
  function updateNavigation(keyCode) {
    const { isNext } = getNavigationDirection(keyCode);
    isNavigatingByKeyboard.value = true;
    const navigationDirection = isNext ? 1 : -1;
    const newNavigationDirectionIndex = currentNavigableNodeIndex.value + navigationDirection;
    const totalNodes = navigableNodes.value.length;
    const newNodePosition = virtualizedList ? getNodePositionWithLooplessNavigation(newNavigationDirectionIndex, totalNodes) : getNodePositionWithLoopNavigation(newNavigationDirectionIndex, totalNodes);
    setCurrentNavigableNode(newNodePosition);
    scrollCurrentNavigableNodeIntoView();
    focusNavigableNode();
  }
  function ensureCanNavigate(canNavigateCallBack) {
    return () => {
      if (!canNavigate.value) return;
      canNavigateCallBack();
    };
  }
  function handleNavigationArrows(keyCode) {
    const navigate = ensureCanNavigate(() => {
      const { emptyDirection } = getNavigationDirection(keyCode);
      if (emptyDirection) return;
      updateNavigation(keyCode);
      setCurrentItem();
      emitCallback(keyCode);
    });
    navigate();
  }
  function handleNavigationKeyboard(event) {
    const navigate = ensureCanNavigate(() => {
      const { code: keyCode } = event;
      const { emptyDirection } = getNavigationDirection(keyCode);
      if (emptyDirection) return;
      event.preventDefault();
      updateNavigation(keyCode);
      setCurrentItem();
      emitCallback(event);
    });
    navigate();
  }
  function resetKeyboardNavigation() {
    isNavigatingByKeyboard.value = false;
    currentNavigableNodeIndex.value = 0;
  }
  function refreshNavigationNodes() {
    if (canNavigate.value && isNavigatingByKeyboard.value) {
      const newIndexOfCurrentItem = navigableNodes.value.findIndex((item) => {
        return getItemIndexByNode(item) === getItemIndexByNode(currentNavigableNode.value);
      });
      setCurrentNavigableNode(newIndexOfCurrentItem);
      focusNavigableNode();
    }
  }
  watch(
    itemsListRef,
    () => {
      itemsListNodes.value = (itemsListRef == null ? void 0 : itemsListRef.value) || [];
      refreshNavigationNodes();
    },
    {
      deep: true,
      immediate: true
    }
  );
  watch(computedItemsList, async () => {
    await nextTick();
    navigableNodes.value = itemsListNodes.value;
    refreshNavigationNodes();
  });
  watch(
    () => canNavigate.value,
    () => {
      handleInitialItem(initialPosition);
    }
  );
  return {
    handleNavigationKeyboard,
    handleNavigationArrows,
    handleNavigationByItemIndex,
    setInitialFocusByNodeIndex,
    resetKeyboardNavigation,
    scrollCurrentNavigableNodeIntoView,
    currentItemOfList,
    currentItemOfListIndex,
    directions,
    isFirstNode,
    isLastNode,
    currentNavigableNodeIndex,
    currentNavigableNode
  };
}
export {
  useListNavigation as u
};
